import { useRef } from "react";
import type { StrandLossCode } from "../../strand-losses-manager/models/StrandAsset";
import { useDataEntryContext } from "../context/reducer.provider";
import { useCasterDataEntryActions } from "./useCasterDataEntryActions";
import { useJustifyStrandLossesMutation } from "./useJustifyStrandLossesMutation";

export const useJustifyAllStrandLosses = () => {
  const {
    state: {
      deProcess,
      showJustifyAllStrandLosses,
      isJustifyingAllStrandLosses,
    },
    actions: {
      setShowJustifyAllStrandLosses,
      setIsJustifyingAllStrandLosses,
      setNotificationMessage,
      setErrorMessage,
    },
  } = useDataEntryContext();
  const { refreshData } = useCasterDataEntryActions();
  const justifyStrandLossesMutation = useJustifyStrandLossesMutation();
  const isSubmittingRef = useRef(false);

  const process = deProcess?.process;
  const canJustifyAll = process?.CanJustifyStrandLosses === true;

  const openJustifyAll = () => {
    if (isJustifyingAllStrandLosses || showJustifyAllStrandLosses) return;

    setShowJustifyAllStrandLosses(true);
  };

  const closeJustifyAll = () => {
    if (isJustifyingAllStrandLosses) return;

    setShowJustifyAllStrandLosses(false);
  };

  const justifyAll = async (
    reason: StrandLossCode,
    comments: string | null,
  ) => {
    if (isSubmittingRef.current) return;

    if (!process) {
      setErrorMessage(
        "Unable to justify strand losses because the Data Entry payload is incomplete. Refresh and try again.",
      );
      return;
    }

    isSubmittingRef.current = true;
    setIsJustifyingAllStrandLosses(true);
    setErrorMessage("");
    try {
      await justifyStrandLossesMutation.mutateAsync({
        processId: process.ProcessId,
        strandLossCodeId: reason.StrandLossCodeId,
        comments,
      });
      setNotificationMessage(
        `All strand losses justified as ${reason.StrandLossCodeName}`,
      );
      await refreshData();
      setShowJustifyAllStrandLosses(false);
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Failed to justify strand losses",
      );
    } finally {
      isSubmittingRef.current = false;
      setIsJustifyingAllStrandLosses(false);
    }
  };

  return {
    canJustifyAll,
    isJustifyingAll: isJustifyingAllStrandLosses,
    showJustifyAll: showJustifyAllStrandLosses,
    openJustifyAll,
    closeJustifyAll,
    justifyAll,
  };
};
